import { useState } from "react";

import { ChevronRight, ChevronDown, Folder, FolderPlus, Edit2, Trash2, FolderOpen } from "lucide-react";

import { MemoryManager } from "@/components/memories/MemoryManager";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { supabase } from "@/integrations/supabase/client";

interface FolderNode {
  id: string;
  name: string;
  parent_folder_id: string | null;
  depth: number;
  children?: FolderNode[];
  projectCount?: number;
}

interface FolderTreeProps {
  folders: FolderNode[];
  selectedFolderId: string | null;
  onSelectFolder: (folderId: string | null) => void;
  workspaceId: string;
  onRefresh: () => void;
}

export const FolderTree = ({
  folders,
  selectedFolderId,
  onSelectFolder,
  workspaceId,
  onRefresh,
}: FolderTreeProps) => {
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [editingFolder, setEditingFolder] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [creatingFolder, setCreatingFolder] = useState<string | null>(null);
  const [newFolderName, setNewFolderName] = useState("");
  const [deletingFolder, setDeletingFolder] = useState<string | null>(null);

  const toggleExpand = (folderId: string) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedFolders(newExpanded);
  };

  const handleCreateFolder = async (parentId: string | null) => {
    if (!newFolderName.trim()) return;

    try {
      const { error } = await supabase.from("folders").insert({
        workspace_id: workspaceId,
        name: newFolderName.trim(),
        parent_folder_id: parentId,
      });

      if (error) throw error;

      toast.success("Folder created");
      setNewFolderName("");
      setCreatingFolder(null);
      onRefresh();
    } catch (error: any) {
      toast.error(error.message || "Failed to create folder");
    }
  };

  const handleRenameFolder = async (folderId: string) => {
    if (!editName.trim()) return;

    try {
      const { error } = await supabase
        .from("folders")
        .update({ name: editName.trim() })
        .eq("id", folderId);

      if (error) throw error;

      toast.success("Folder renamed");
      setEditingFolder(null);
      setEditName("");
      onRefresh();
    } catch (error: any) {
      toast.error(error.message || "Failed to rename folder");
    }
  };

  const handleDeleteFolder = async () => {
    if (!deletingFolder) return;

    try {
      const { error } = await supabase
        .from("folders")
        .delete()
        .eq("id", deletingFolder);

      if (error) throw error;

      toast.success("Folder deleted");
      setDeletingFolder(null);
      if (selectedFolderId === deletingFolder) {
        onSelectFolder(null);
      }
      onRefresh();
    } catch (error: any) {
      toast.error(error.message || "Failed to delete folder");
    }
  };

  const renderFolder = (folder: FolderNode, level: number = 0) => {
    const isExpanded = expandedFolders.has(folder.id);
    const hasChildren = folder.children && folder.children.length > 0;
    const isSelected = selectedFolderId === folder.id;
    const isEditing = editingFolder === folder.id;
    const isCreating = creatingFolder === folder.id;

    return (
      <div key={folder.id}>
        <div
          className={`flex items-center gap-1 py-1 px-2 rounded-md hover:bg-accent/50 ${
            isSelected ? "bg-accent" : ""
          }`}
          style={{ paddingLeft: `${level * 20 + 8}px` }}
        >
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5 shrink-0"
            onClick={() => hasChildren && toggleExpand(folder.id)}
            disabled={!hasChildren}
          >
            {hasChildren ? (
              isExpanded ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )
            ) : (
              <span className="w-3" />
            )}
          </Button>

          {isEditing ? (
            <Input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleRenameFolder(folder.id);
                if (e.key === "Escape") setEditingFolder(null);
              }}
              onBlur={() => handleRenameFolder(folder.id)}
              className="h-6 text-sm flex-1"
              autoFocus
            />
          ) : (
            <>
              <Folder className="h-4 w-4 shrink-0 text-muted-foreground" />
              <button
                onClick={() => onSelectFolder(folder.id)}
                className="text-sm flex-1 text-left truncate hover:text-foreground"
              >
                {folder.name}
                {folder.projectCount !== undefined && (
                  <span className="text-xs text-muted-foreground ml-1">
                    ({folder.projectCount})
                  </span>
                )}
              </button>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100">
                <div onClick={(e) => e.stopPropagation()}>
                  <MemoryManager
                    folderId={folder.id}
                    scopeLabel={folder.name}
                  />
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCreatingFolder(folder.id);
                  }}
                  disabled={folder.depth >= 15}
                >
                  <FolderPlus className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingFolder(folder.id);
                    setEditName(folder.name);
                  }}
                >
                  <Edit2 className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeletingFolder(folder.id);
                  }}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </>
          )}
        </div>

        {isCreating && (
          <div className="py-1 px-2" style={{ paddingLeft: `${(level + 1) * 20 + 8}px` }}>
            <Input
              placeholder="New folder name"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleCreateFolder(folder.id);
                if (e.key === "Escape") setCreatingFolder(null);
              }}
              onBlur={() => handleCreateFolder(folder.id)}
              className="h-6 text-sm"
              autoFocus
            />
          </div>
        )}

        {isExpanded && hasChildren && (
          <div>
            {folder.children!.map((child) => renderFolder(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const buildTree = (folders: FolderNode[]): FolderNode[] => {
    const folderMap = new Map<string, FolderNode>();
    folders.forEach((f) => folderMap.set(f.id, { ...f, children: [] }));

    const roots: FolderNode[] = [];
    folderMap.forEach((folder) => {
      if (folder.parent_folder_id === null) {
        roots.push(folder);
      } else {
        const parent = folderMap.get(folder.parent_folder_id);
        if (parent) {
          parent.children!.push(folder);
        }
      }
    });

    return roots;
  };

  const tree = buildTree(folders);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium">Folders</h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setCreatingFolder("root")}
          className="h-7"
        >
          <FolderPlus className="h-3 w-3 mr-1" />
          New
        </Button>
      </div>

      <button
        onClick={() => onSelectFolder(null)}
        className={`w-full flex items-center gap-2 py-1 px-2 rounded-md hover:bg-accent/50 text-sm ${
          selectedFolderId === null ? "bg-accent" : ""
        }`}
      >
        <FolderOpen className="h-4 w-4 text-muted-foreground" />
        <span>All Projects</span>
      </button>

      {creatingFolder === "root" && (
        <div className="py-1 px-2">
          <Input
            placeholder="New folder name"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCreateFolder(null);
              if (e.key === "Escape") setCreatingFolder(null);
            }}
            onBlur={() => handleCreateFolder(null)}
            className="h-6 text-sm"
            autoFocus
          />
        </div>
      )}

      <div className="space-y-0.5">{tree.map((folder) => renderFolder(folder))}</div>

      <AlertDialog open={!!deletingFolder} onOpenChange={() => setDeletingFolder(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Folder</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete the folder and move all its subfolders and projects to the parent folder.
              Are you sure?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteFolder}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
